/**
 * 订单详情抽屉 - 状态变更记录区块（`GET /orders/:id/status-logs`）
 *
 * 服务端已按记录时间倒序、上限 50 条，这里不再排序也不截断。
 * 三态分别渲染：加载中一行占位、失败摆出服务端原文 + 就地重试、
 * 一条都没有则整块不渲染。失败绝不能演成空态——「这单没改过状态」和
 * 「你没读到它改过没有」是两句话，合并成一态就是把后者伪装成前者。
 */
import { ORDER_STATUS_STYLES, fmtDateTime } from '../utils';
import type { OrderStatusLogItem } from '../../../../../api/orderApi';

interface Props {
  logs: OrderStatusLogItem[];
  isLoading: boolean;
  errorMessage: string;
  onRetry: () => void;
}

export default function StatusLogSection({ logs, isLoading, errorMessage, onRetry }: Props) {
  if (isLoading) {
    return (
      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">状态变更记录</p>
        <p className="text-sm text-gray-400">加载中…</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">状态变更记录</p>
        <div className="flex items-center justify-between gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
          <p className="text-sm text-rose-600 flex items-center gap-1.5 min-w-0">
            <i className="ri-error-warning-line text-sm shrink-0" />
            <span className="truncate">{errorMessage}</span>
          </p>
          <button
            onClick={onRetry}
            className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer whitespace-nowrap shrink-0 transition-colors"
          >
            <i className="ri-refresh-line text-sm" />
            重试
          </button>
        </div>
      </div>
    );
  }

  if (logs.length === 0) return null;

  return (
    <div className="pt-2 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">状态变更记录</p>
      <ul className="space-y-3">
        {logs.map(log => {
          // 时间线落在哪个颜色上按**新状态**取：读者关心的是「变成了什么」
          const style = ORDER_STATUS_STYLES[log.newStatus] ?? { dot: 'bg-gray-300', text: 'text-gray-500' };
          const meta = [
            log.changeReason,
            log.changedBy || null,
            fmtDateTime(log.createdAt),
          ].filter(Boolean).join(' · ');
          return (
            <li key={log.id} className="flex items-start gap-2">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
              <div className="min-w-0">
                <p className={`text-sm font-medium ${style.text}`}>{log.oldStatus} → {log.newStatus}</p>
                <p className="text-xs text-gray-400 mt-0.5 break-words">{meta}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
